import { Button } from '@components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Textarea } from '@components/ui/textarea';
import { useCreateNote } from '@hooks/useNotes';
import React, { useCallback, useState } from 'react';

interface AddNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecordId: string;
}

export function AddNoteDialog({ isOpen, onClose, caseRecordId }: AddNoteDialogProps) {
  const [content, setContent] = useState('');
  const createNoteMutation = useCreateNote(caseRecordId);

  const handleConfirm = useCallback(async () => {
    if (!content.trim() || !caseRecordId) {
      return;
    }

    createNoteMutation.mutate(content.trim(), {
      onSuccess: () => {
        setContent('');
        onClose();
      },
    });
  }, [content, caseRecordId, createNoteMutation, onClose]);

  const handleClose = useCallback(() => {
    setContent('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Case Note</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <Textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Enter your note here..."
            className="min-h-[120px]"
            disabled={createNoteMutation.isPending}
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={createNoteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={createNoteMutation.isPending || !content.trim()}
          >
            {createNoteMutation.isPending ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
